import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

const STORAGE_KEY = 'emojisurveys_contacto_enviado';
const COOLDOWN_HOURS = 24;

// ─── i18n ──────────────────────────────────────────────────────────────────────

const translations = {
  en: {
    heroTitle: 'Contact',
    heroDescription:
      "Have questions? We're here to help. Reach out and we'll get back to you as soon as possible.",
    infoTitle: '📞 Contact Information',
    formTitle: '✉️ Send Us a Message',
    labelName: 'Full Name *',
    placeholderName: 'Your full name',
    labelEmail: 'Email *',
    placeholderEmail: 'you@email.com',
    labelMessage: 'Message *',
    placeholderMessage: 'Tell us how we can help you…',
    buttonSending: '📤 Sending…',
    buttonSend: '🚀 Send Message',
    errorGeneral: 'There was an error sending your message. Please try again.',
    blockedTitleSuccess: 'Message sent successfully!',
    blockedTitleAlready: 'You already sent a message',
    blockedTextSuccess:
      "Thank you for reaching out. We'll reply within the next 24 hours.",
    blockedTextAlready:
      "We already received your message. We're working on your reply.",
    blockedTimer: (h) => `⏳ You can send another message in ${h} hour${h !== 1 ? 's' : ''}`,
    faqTitle: '❓ Frequently Asked Questions',
    faqSubtitle:
      'Find quick answers to the most common questions about EmojiSurveys.',
    contactInfo: [
      { icon: '📧', label: 'Email',    value: 'emojisurveys@outlook.com', link: 'mailto:emojisurveys@outlook.com' },
      { icon: '📍', label: 'Location', value: 'Hermosillo, Sonora, Mexico', link: null },
      { icon: '⏰', label: 'Hours',    value: 'Mon – Fri: 9:00 AM – 6:00 PM', link: null },
    ],
    faqs: [
      { question: 'How do I get started with EmojiSurveys?',      answer: "It's very simple. Just sign up on our platform, create your first survey using our visual editor, and share the link with your customers." },
      { question: 'Do you offer technical support?',               answer: 'Absolutely. Our support team is available Monday through Friday from 9:00 AM to 6:00 PM (Mexico time).' },
      { question: 'Can I customize the design of my surveys?',     answer: 'Yes, we offer multiple customization options including colors, fonts, logos, and element layouts.' },
      { question: 'Are there limits on the number of responses?',  answer: "It depends on the plan you choose. Our free plan includes a set number of responses per month." },
      { question: 'Is my data secure?',                            answer: 'Security is our top priority. We use SSL encryption and all data is stored on secure servers.' },
    ],
  },
  es: {
    heroTitle: 'Contacto',
    heroDescription:
      '¿Tienes preguntas? Estamos aqui para ayudarte. Contactanos y te responderemos lo antes posible.',
    infoTitle: '📞 Informacion de Contacto',
    formTitle: '✉️ Envianos un Mensaje',
    labelName: 'Nombre Completo *',
    placeholderName: 'Tu nombre completo',
    labelEmail: 'Email *',
    placeholderEmail: 'tu@email.com',
    labelMessage: 'Mensaje *',
    placeholderMessage: 'Cuentanos como podemos ayudarte…',
    buttonSending: '📤 Enviando…',
    buttonSend: '🚀 Enviar Mensaje',
    errorGeneral: 'Hubo un error al enviar tu mensaje. Por favor intenta de nuevo.',
    blockedTitleSuccess: '¡Mensaje enviado con exito!',
    blockedTitleAlready: 'Ya enviaste un mensaje',
    blockedTextSuccess:
      'Gracias por contactarnos. Te responderemos en las proximas 24 horas.',
    blockedTextAlready:
      'Ya recibimos tu mensaje. Estamos trabajando en tu respuesta.',
    blockedTimer: (h) => `⏳ Podras enviar otro mensaje en ${h} hora${h !== 1 ? 's' : ''}`,
    faqTitle: '❓ Preguntas Frecuentes',
    faqSubtitle:
      'Encuentra respuestas rapidas a las preguntas mas comunes sobre EmojiSurveys.',
    contactInfo: [
      { icon: '📧', label: 'Email',     value: 'emojisurveys@outlook.com',   link: 'mailto:emojisurveys@outlook.com' },
      { icon: '📍', label: 'Ubicacion', value: 'Hermosillo, Sonora, Mexico', link: null },
      { icon: '⏰', label: 'Horario',   value: 'Lun - Vie: 9:00 - 18:00',   link: null },
    ],
    faqs: [
      { question: '¿Como puedo empezar a usar EmojiSurveys?',        answer: 'Es muy sencillo. Solo necesitas registrarte en nuestra plataforma, crear tu primera encuesta usando nuestro editor visual, y compartir el enlace con tus clientes.' },
      { question: '¿Ofrecen soporte tecnico?',                        answer: 'Por supuesto. Nuestro equipo de soporte esta disponible de lunes a viernes de 9:00 a 18:00 (hora de Mexico).' },
      { question: '¿Puedo personalizar el diseno de mis encuestas?',  answer: 'Si, ofrecemos multiples opciones de personalizacion incluyendo colores, fuentes, logos, y disposicion de elementos.' },
      { question: '¿Hay limites en el numero de respuestas?',         answer: 'Depende del plan que elijas. Nuestro plan gratuito incluye respuestas por mes.' },
      { question: '¿Los datos estan seguros?',                        answer: 'La seguridad es nuestra prioridad. Utilizamos encriptacion SSL y todos los datos se almacenan en servidores seguros.' },
    ],
  },
};

/** Returns 'es' if the browser language starts with 'es', otherwise 'en'. */
function detectLang() {
  const lang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
  return lang.startsWith('es') ? 'es' : 'en';
}

// ─── Helpers de localStorage ───────────────────────────────────────────────────

function getSubmissionRecord() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSubmissionRecord() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ sentAt: Date.now() }));
  } catch { /* localStorage not available */ }
}

function isLocallyBlocked() {
  const record = getSubmissionRecord();
  if (!record) return false;
  const hoursElapsed = (Date.now() - record.sentAt) / (1000 * 60 * 60);
  return hoursElapsed < COOLDOWN_HOURS;
}

function hoursRemaining() {
  const record = getSubmissionRecord();
  if (!record) return 0;
  const elapsed = (Date.now() - record.sentAt) / (1000 * 60 * 60);
  return Math.max(0, Math.ceil(COOLDOWN_HOURS - elapsed));
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  heroSection: { position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  heroOverlay: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%)' },
  heroContent: { position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'white', padding: '16px', maxWidth: '600px' },
  heroTitle: { fontSize: '3rem', fontWeight: 'bold', marginBottom: '20px', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' },
  heroDescription: { fontSize: '1.2rem', lineHeight: '1.6', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' },
  emojiHero: { fontSize: '3.5rem', marginBottom: '20px' },
  container: { maxWidth: '1200px', margin: '0 auto', padding: '0 20px' },
  contactSection: { padding: '80px 0', backgroundColor: 'white' },
  contactGrid: { display: 'grid', gap: '60px', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', alignItems: 'start' },
  contactInfo: { padding: '40px', borderRadius: '20px', background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)' },
  contactForm: { padding: '40px', borderRadius: '20px', background: 'white', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', border: '1px solid rgba(0,0,0,0.05)' },
  sectionTitle: { fontSize: '2.2rem', fontWeight: 'bold', marginBottom: '30px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  contactItem: { display: 'flex', alignItems: 'center', marginBottom: '30px', padding: '20px', borderRadius: '15px', background: 'white', boxShadow: '0 5px 15px rgba(0,0,0,0.08)', transition: 'all 0.3s ease' },
  contactItemHover: { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.12)' },
  contactIcon: { fontSize: '2rem', marginRight: '20px', minWidth: '60px', height: '60px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  contactDetails: { flex: 1 },
  contactLabel: { fontSize: '0.9rem', color: '#64748b', marginBottom: '5px', fontWeight: '500' },
  contactValue: { fontSize: '1.1rem', color: '#1e293b', fontWeight: '600' },
  formGroup: { marginBottom: '25px' },
  label: { display: 'block', marginBottom: '8px', fontSize: '0.95rem', fontWeight: '600', color: '#374151' },
  input: { width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '1rem', transition: 'all 0.3s ease', backgroundColor: '#f9fafb', outline: 'none', boxSizing: 'border-box' },
  inputFocus: { borderColor: '#667eea', backgroundColor: 'white', boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)' },
  textarea: { minHeight: '120px', resize: 'vertical', fontFamily: 'inherit' },
  submitButton: { width: '100%', padding: '16px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '1.1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 5px 15px rgba(102, 126, 234, 0.4)' },
  submitButtonHover: { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(102, 126, 234, 0.5)' },
  submitButtonDisabled: { opacity: 0.5, cursor: 'not-allowed', transform: 'none', boxShadow: 'none', background: '#94a3b8' },
  errorMessage: { padding: '15px', backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '10px', color: '#991b1b', marginBottom: '20px', fontSize: '0.95rem', fontWeight: '500' },
  blockedBanner: { padding: '30px', backgroundColor: '#f0f9ff', border: '2px solid #bae6fd', borderRadius: '15px', textAlign: 'center' },
  blockedEmoji: { fontSize: '3rem', marginBottom: '15px' },
  blockedTitle: { fontSize: '1.3rem', fontWeight: '700', color: '#0369a1', marginBottom: '10px' },
  blockedText: { fontSize: '1rem', color: '#0284c7', lineHeight: '1.6' },
  blockedTimer: { marginTop: '15px', fontSize: '0.9rem', color: '#64748b', fontWeight: '500' },
  faqSection: { padding: '80px 0', background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)' },
  faqGrid: { display: 'grid', gap: '20px', maxWidth: '800px', margin: '0 auto' },
  faqItem: { background: 'white', borderRadius: '15px', padding: '25px', boxShadow: '0 5px 15px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'all 0.3s ease' },
  faqItemHover: { boxShadow: '0 8px 25px rgba(0,0,0,0.12)' },
  faqQuestion: { fontSize: '1.1rem', fontWeight: '600', color: '#1e293b', marginBottom: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  faqAnswer: { fontSize: '1rem', color: '#64748b', lineHeight: '1.6' },
  expandIcon: { fontSize: '1.2rem', transition: 'transform 0.3s ease' },
  expandIconRotated: { transform: 'rotate(180deg)' },
};

// ─── Component ─────────────────────────────────────────────────────────────────

function Contacto() {
  const [lang]                              = useState(detectLang);
  const t                                   = translations[lang];

  const [formData, setFormData]             = useState({ nombre: '', email: '', mensaje: '' });
  const [focusedField, setFocusedField]     = useState(null);
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [isSubmitted, setIsSubmitted]       = useState(false);
  const [submitError, setSubmitError]       = useState(null);
  const [isBlocked, setIsBlocked]           = useState(false);
  const [hoveredContact, setHoveredContact] = useState(null);
  const [hoveredFaq, setHoveredFaq]         = useState(null);
  const [expandedFaq, setExpandedFaq]       = useState(null);

  useEffect(() => {
    setIsBlocked(isLocallyBlocked());
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (submitError) setSubmitError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLocallyBlocked()) {
      setIsBlocked(true);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await addDoc(collection(db, 'mensajes'), {
        nombre:   formData.nombre.trim(),
        email:    formData.email.toLowerCase().trim(),
        mensaje:  formData.mensaje.trim(),
        creadoEn: serverTimestamp(),
        leido:    false,
      });

      saveSubmissionRecord();
      setIsBlocked(true);
      setIsSubmitted(true);
      setFormData({ nombre: '', email: '', mensaje: '' });

    } catch (error) {
      console.error('Firestore error:', error);
      setSubmitError(t.errorGeneral);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>

      {/* Hero */}
      <div style={styles.heroSection}>
        <div style={styles.heroOverlay} />
        <div style={styles.heroContent}>
          <div style={styles.emojiHero}>💬</div>
          <h1 style={styles.heroTitle}>{t.heroTitle}</h1>
          <p style={styles.heroDescription}>{t.heroDescription}</p>
        </div>
      </div>

      {/* Contact Section */}
      <section style={styles.contactSection}>
        <div style={styles.container}>
          <div style={styles.contactGrid}>

            {/* Info */}
            <div>
              <div style={styles.contactInfo}>
                <h2 style={styles.sectionTitle}>{t.infoTitle}</h2>
                {t.contactInfo.map((item, index) => (
                  <div
                    key={index}
                    style={{ ...styles.contactItem, ...(hoveredContact === index ? styles.contactItemHover : {}) }}
                    onMouseEnter={() => setHoveredContact(index)}
                    onMouseLeave={() => setHoveredContact(null)}
                  >
                    <div style={styles.contactIcon}>{item.icon}</div>
                    <div style={styles.contactDetails}>
                      <div style={styles.contactLabel}>{item.label}</div>
                      {item.link ? (
                        <a href={item.link} style={{ ...styles.contactValue, textDecoration: 'none', color: '#667eea' }}>
                          {item.value}
                        </a>
                      ) : (
                        <div style={styles.contactValue}>{item.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div>
              <div style={styles.contactForm}>
                <h2 style={styles.sectionTitle}>{t.formTitle}</h2>

                {isBlocked ? (
                  <div style={styles.blockedBanner}>
                    <div style={styles.blockedEmoji}>{isSubmitted ? '✅' : '🔒'}</div>
                    <div style={styles.blockedTitle}>
                      {isSubmitted ? t.blockedTitleSuccess : t.blockedTitleAlready}
                    </div>
                    <p style={styles.blockedText}>
                      {isSubmitted ? t.blockedTextSuccess : t.blockedTextAlready}
                    </p>
                    <p style={styles.blockedTimer}>{t.blockedTimer(hoursRemaining())}</p>
                  </div>
                ) : (
                  <>
                    {submitError && <div style={styles.errorMessage}>❌ {submitError}</div>}

                    <form onSubmit={handleSubmit}>
                      <div style={styles.formGroup}>
                        <label style={styles.label} htmlFor="nombre">{t.labelName}</label>
                        <input
                          type="text" id="nombre" name="nombre"
                          value={formData.nombre} onChange={handleInputChange}
                          onFocus={() => setFocusedField('nombre')} onBlur={() => setFocusedField(null)}
                          style={{ ...styles.input, ...(focusedField === 'nombre' ? styles.inputFocus : {}) }}
                          required placeholder={t.placeholderName}
                        />
                      </div>

                      <div style={styles.formGroup}>
                        <label style={styles.label} htmlFor="email">{t.labelEmail}</label>
                        <input
                          type="email" id="email" name="email"
                          value={formData.email} onChange={handleInputChange}
                          onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
                          style={{ ...styles.input, ...(focusedField === 'email' ? styles.inputFocus : {}) }}
                          required placeholder={t.placeholderEmail}
                        />
                      </div>

                      <div style={styles.formGroup}>
                        <label style={styles.label} htmlFor="mensaje">{t.labelMessage}</label>
                        <textarea
                          id="mensaje" name="mensaje"
                          value={formData.mensaje} onChange={handleInputChange}
                          onFocus={() => setFocusedField('mensaje')} onBlur={() => setFocusedField(null)}
                          style={{ ...styles.input, ...styles.textarea, ...(focusedField === 'mensaje' ? styles.inputFocus : {}) }}
                          required placeholder={t.placeholderMessage}
                        />
                      </div>

                      <button
                        type="submit" disabled={isSubmitting}
                        style={{ ...styles.submitButton, ...(isSubmitting ? styles.submitButtonDisabled : {}) }}
                        onMouseEnter={(e) => { if (!isSubmitting) Object.assign(e.target.style, styles.submitButtonHover); }}
                        onMouseLeave={(e) => { if (!isSubmitting) { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)'; } }}
                      >
                        {isSubmitting ? t.buttonSending : t.buttonSend}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={styles.faqSection}>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={styles.sectionTitle}>{t.faqTitle}</h2>
            <p style={{ fontSize: '1.1rem', color: '#64748b', maxWidth: '600px', margin: '0 auto', lineHeight: '1.6' }}>
              {t.faqSubtitle}
            </p>
          </div>
          <div style={styles.faqGrid}>
            {t.faqs.map((faq, index) => (
              <div
                key={index}
                style={{ ...styles.faqItem, ...(hoveredFaq === index ? styles.faqItemHover : {}) }}
                onMouseEnter={() => setHoveredFaq(index)}
                onMouseLeave={() => setHoveredFaq(null)}
                onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
              >
                <div style={styles.faqQuestion}>
                  <span>{faq.question}</span>
                  <span style={{ ...styles.expandIcon, ...(expandedFaq === index ? styles.expandIconRotated : {}) }}>⌄</span>
                </div>
                {expandedFaq === index && <div style={styles.faqAnswer}>{faq.answer}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}

export default Contacto;